import { useEffect, useRef, useState } from "preact/hooks";

import { ConnectionErrorScreen } from "../components/ConnectionErrorScreen";
import { PasswordToggleInput } from "../components/PasswordToggleInput";
import { useFocusHeadingOnMount } from "../hooks/useFocusHeadingOnMount";
import { ApiError, postLogin } from "../lib/api";
import {
  CONNECTIVITY_ERROR_MESSAGE,
  RETURNING_LOGIN_FOOTER_HINT,
  RETURNING_LOGIN_GENERIC_ERROR,
  RETURNING_LOGIN_HEADING,
  RETURNING_LOGIN_NAME_LABEL,
  RETURNING_LOGIN_PASSKEY_BUTTON,
  RETURNING_LOGIN_PASSWORD_LABEL,
  RETURNING_LOGIN_PRIMARY_BUTTON,
  RETURNING_LOGIN_RATE_LIMIT_ERROR,
  RETURNING_LOGIN_SPACE_HINT,
} from "../lib/authCopy";
import {
  isConditionalMediationAvailable,
  isWebAuthnSupported,
  signInWithPasskey,
} from "../lib/webauthn";

export interface LoginViewProps {
  /** Called after a successful `POST /auth/login` (or a successful
   * one-tap passkey sign-in) — the caller re-fetches `/me`
   * (`useMe().refresh`) rather than this view guessing the next screen
   * itself; the server's fresh `needs_onboarding`/`password_reset_required`
   * flags are the single source of truth for where a returning login
   * actually lands (UX §8.2 / ADR-002 A4 §4b). */
  onLoggedIn: () => void;
}

type LoginError = "generic" | "rateLimit" | null;

/** "conditional" — this browser supports autofill-style discoverable-
 * credential suggestions: a background conditional `get()` feeds the
 * platform's own suggestion chip on the sign-in-name field, AND the
 * visible "Use Face ID or fingerprint" button is drawn too (UX review
 * 2026-10: the chip alone was invisible to people who never tap the
 * field, so modern Chrome/Safari showed no Face ID option at all).
 * "button" — WebAuthn is supported but conditional mediation isn't: the
 * visible button only. "none" — no WebAuthn support at all; the screen
 * opens straight on the sign-in-name field, unchanged from sprint-007 D4. */
type PasskeyMode = "conditional" | "button" | "none";

/**
 * Sprint-007 D4/D6 (F4-1 + F4-2, ADR-002 Amendment 4 §7) — the Returning
 * Login screen (UX §2). Replaces `LoggedOutScreen`'s retired "there is no
 * login form" state.
 *
 * Passkey one-tap (D6): "Passkey button goes first, above the typed form,
 * when available" (UX §2.1). There is no client-side way to ask "does
 * THIS browser have an enrolled credential for this origin" synchronously
 * before an attempt — conditional-UI browsers solve this natively (the
 * suggestion simply doesn't appear if nothing is enrolled). Since the UX
 * review of 2026-10 the visible "Use Face ID or fingerprint" button is
 * drawn on EVERY WebAuthn-capable browser (conditional-UI ones keep the
 * background autofill request too; pressing the button aborts it first —
 * see `handlePasskeyButton`). A tap with nothing
 * enrolled fails exactly like any other cancelled ceremony (silent reset,
 * no error — UX §2.2's "no error banner at all... a normal, silent path,
 * not a failure," which this view applies to EVERY passkey failure reason
 * on this screen, not only an explicit cancel, matching the passkey-login
 * endpoint's own one-generic-401-for-every-reason contract). Flagged in
 * the PR as a documented, reasoned fallback shape, not a spec deviation.
 */
export function LoginView({ onLoggedIn }: LoginViewProps) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<LoginError>(null);
  const [connectivityMessage, setConnectivityMessage] = useState<string | null>(null);
  const [passkeyMode, setPasskeyMode] = useState<PasskeyMode>("none");
  const [passkeyBusy, setPasskeyBusy] = useState(false);
  // UX review 2026-10 — set on a rejected (401) login whose typed name had
  // a space in it (the classic "typed my display name" miss); drives the
  // extra hint UNDER the generic error. Client-side only, from what they
  // typed — says nothing about which accounts exist.
  const [typedNameHadSpace, setTypedNameHadSpace] = useState(false);
  // Bumped to (re)start the background conditional request — after an
  // explicit button ceremony fails/cancels, autofill should work again.
  const [conditionalRun, setConditionalRun] = useState(0);
  // The in-flight conditional request, so the button can abort it and
  // wait for it to settle first: WebAuthn allows ONE pending ceremony per
  // page, and a modal `get()` started while the conditional one is still
  // pending rejects (Chrome: "A request is already pending").
  const conditionalRef = useRef<{ controller: AbortController; settled: Promise<void> } | null>(
    null,
  );
  const headingRef = useRef<HTMLHeadingElement>(null);

  useFocusHeadingOnMount(headingRef);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!isWebAuthnSupported()) return;
      const conditional = await isConditionalMediationAvailable();
      if (cancelled) return;
      setPasskeyMode(conditional ? "conditional" : "button");
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Background conditional-UI (autofill) one-tap sign-in — the browser
  // shows its own native suggestion chip near the sign-in-name field
  // (wired via that field's `autocomplete="username webauthn"` below);
  // this effect just keeps the promise alive and aborts it on unmount or
  // whenever this screen goes away, so it never resolves against a
  // component that's no longer there to call `onLoggedIn()`.
  useEffect(() => {
    if (passkeyMode !== "conditional") return undefined;
    const controller = new AbortController();
    const settled = signInWithPasskey({ mediation: "conditional", signal: controller.signal })
      // Unguarded on purpose: a request that got as far as resolving has
      // already minted a session server-side, so refresh `/me` regardless.
      .then(() => onLoggedIn())
      .catch(() => {
        // UX §2.2: a cancelled/unavailable/no-match conditional suggestion
        // is never an error state — the typed form is already the
        // fallback UI, showing right alongside it the whole time. An
        // AbortError from the button (below) or unmount lands here too.
      });
    const entry = { controller, settled };
    conditionalRef.current = entry;
    return () => {
      controller.abort();
      if (conditionalRef.current === entry) conditionalRef.current = null;
    };
  }, [passkeyMode, conditionalRun]);

  async function handlePasskeyButton() {
    if (passkeyBusy) return;
    setPasskeyBusy(true);
    // Only one WebAuthn ceremony may be pending: abort the background
    // conditional request and WAIT for it to actually settle before
    // starting the modal one.
    const pending = conditionalRef.current;
    conditionalRef.current = null;
    if (pending) {
      pending.controller.abort();
      await pending.settled;
    }
    try {
      await signInWithPasskey();
      onLoggedIn();
    } catch {
      // UX §2.2: "the button simply returns to its resting state, exactly
      // as if it were never tapped" — no error copy for ANY passkey-login
      // failure reason on this screen (the endpoint itself returns one
      // generic 401 for every reason, so there is nothing more specific
      // to say even if we wanted to). Re-arm autofill if we aborted it.
      if (pending) setConditionalRun((n) => n + 1);
    } finally {
      setPasskeyBusy(false);
    }
  }

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (!username.trim() || !password || submitting) return;

    setSubmitting(true);
    setError(null);
    setTypedNameHadSpace(false);
    try {
      await postLogin({ username: username.trim(), password });
      onLoggedIn();
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("generic");
        setTypedNameHadSpace(/\s/.test(username.trim()));
        // UX §2.2: "Password field only is cleared on error; the name
        // field is left as typed" — never make someone retype their own
        // name because their password was wrong.
        setPassword("");
      } else if (err instanceof ApiError && err.status === 429) {
        setError("rateLimit");
      } else {
        // UX §2.2: a genuine connectivity failure is a different failure
        // class than a rejected login — reuse `useMe.ts`'s own distinction
        // and copy (`ConnectionErrorScreen`) rather than inventing a third.
        setConnectivityMessage(CONNECTIVITY_ERROR_MESSAGE);
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (connectivityMessage) {
    return (
      <ConnectionErrorScreen
        message={connectivityMessage}
        onRetry={() => setConnectivityMessage(null)}
      />
    );
  }

  return (
    <div class="logged-out-screen">
      {/* Sprint-008 A5 (design-desktop-phase-a.md §4) — wraps this
       * screen's existing content (wordmark, heading, form(s), footer
       * hint) in a card treatment at `≥1024px` ONLY; below that breakpoint
       * `.auth-card` is `display: contents` (see `styles.css`'s own
       * docstring on that rule), so this wrapper changes nothing about
       * the phone layout. */}
      <div class="auth-card">
        <span class="app-wordmark">Hayloft</span>
        <h1 ref={headingRef} tabIndex={-1} class="logged-out-heading">
          {RETURNING_LOGIN_HEADING}
        </h1>

        {passkeyMode !== "none" ? (
          <div class="auth-form">
            <button
              type="button"
              class="primary-btn auth-passkey-btn"
              disabled={passkeyBusy || submitting}
              onClick={() => void handlePasskeyButton()}
            >
              {RETURNING_LOGIN_PASSKEY_BUTTON}
            </button>
            <p class="auth-or-divider" aria-hidden="true">
              or
            </p>
          </div>
        ) : null}

        <form class="auth-form" onSubmit={(e) => void handleSubmit(e)}>
          <label class="admin-field-label" for="login-username">
            {RETURNING_LOGIN_NAME_LABEL}
          </label>
          <input
            id="login-username"
            class="pick-input"
            type="text"
            autocomplete={passkeyMode === "conditional" ? "username webauthn" : "username"}
            value={username}
            required
            aria-required="true"
            disabled={submitting}
            onInput={(e) => setUsername((e.target as HTMLInputElement).value)}
          />

          <label class="admin-field-label" for="login-password">
            {RETURNING_LOGIN_PASSWORD_LABEL}
          </label>
          <PasswordToggleInput
            id="login-password"
            value={password}
            autoComplete="current-password"
            disabled={submitting}
            onInput={setPassword}
          />

          {error === "generic" ? (
            <div class="auth-login-error" role="alert">
              <p class="error-hint">{RETURNING_LOGIN_GENERIC_ERROR}</p>
              {typedNameHadSpace ? (
                <p class="auth-error-detail">{RETURNING_LOGIN_SPACE_HINT}</p>
              ) : null}
            </div>
          ) : null}
          {error === "rateLimit" ? (
            <p class="error-hint" role="alert">
              {RETURNING_LOGIN_RATE_LIMIT_ERROR}
            </p>
          ) : null}

          <button
            type="submit"
            class="primary-btn"
            disabled={!username.trim() || !password || submitting}
          >
            {RETURNING_LOGIN_PRIMARY_BUTTON}
          </button>
        </form>

        <p class="auth-footer-hint">{RETURNING_LOGIN_FOOTER_HINT}</p>
      </div>
    </div>
  );
}
